import type * as React from 'react';

import * as api from '@gitbook/api-client';
import { createPalette, PaletteButton, type PaletteButtonProps } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { type IconDefinition, Pill } from '@gitbook/spine-react';

import { useAssertCurrentOrganizationId, useLoggedInUser } from '../../state';
import { useAgentEnabled } from '../Agent';
import { createPickConversationsIssuesClusterPalette } from '../Conversations';
import { createPickOrganizationMemberPalette } from '../Search';
import { useSelectSpacePalette } from '../Space';

export type ChangeRequestsListFilterState = {
    status?: api.ChangeRequestStatus;
    contributor?: string;
    requestedReview?: string;
    creator?: string;
    space?: string;
    conversationsIssuesCluster?: string;
};

/**
 * Check if filters are set.
 */
export function checkHasFilters(filters: ChangeRequestsListFilterState) {
    return Boolean(
        filters.contributor ||
            filters.space ||
            filters.creator ||
            filters.requestedReview ||
            filters.conversationsIssuesCluster
    );
}

/**
 * UI to filter the list of change requests.
 */
export function ChangeRequestsListFilterButton(
    props: {
        filters: ChangeRequestsListFilterState;
        onFiltersChange: (filters: ChangeRequestsListFilterState) => void;
        /**
         * If false, the user will not be able to filter by space.
         * @default true
         */
        withSpaceFilter?: boolean;
    } & Pick<PaletteButtonProps, 'size' | 'iconOnly'>
): React.ReactElement {
    const { filters, onFiltersChange, withSpaceFilter = true, ...rest } = props;
    const organizationId = useAssertCurrentOrganizationId();
    const docsAgentEnabled = useAgentEnabled();

    const pickSpacePalette = useSelectSpacePalette({
        organizationId,
        selectedSpaceId: filters.space,
        onSpaceSelected: (space) => onFiltersChange({ ...filters, space: space.id }),
    });

    const hasFilters = checkHasFilters(filters);

    return (
        <PaletteButton
            leadingIcon={Icon.Filter}
            kind="blank"
            label="Filter"
            palette={() =>
                createPalette({
                    groups: [
                        {
                            key: 'filters',
                            commands: [
                                {
                                    key: 'creator',
                                    title: 'Creator',
                                    icon: Icon.User,
                                    descendant: () =>
                                        createPickOrganizationMemberPalette({
                                            organizationId,
                                            selectedUserId: filters.creator,
                                            suggestGitBookAgent: docsAgentEnabled,
                                            onPick: (userId) =>
                                                onFiltersChange({
                                                    ...filters,
                                                    creator: userId,
                                                }),
                                        }),
                                },
                                {
                                    key: 'contributor',
                                    title: 'Participant',
                                    icon: Icon.Users,
                                    descendant: () =>
                                        createPickOrganizationMemberPalette({
                                            organizationId,
                                            selectedUserId: filters.contributor,
                                            suggestGitBookAgent: docsAgentEnabled,
                                            onPick: (userId) =>
                                                onFiltersChange({
                                                    ...filters,
                                                    contributor: userId,
                                                }),
                                        }),
                                },
                                {
                                    key: 'requestedReview',
                                    title: 'Awaited review',
                                    icon: Icon.RoleReviewer,
                                    descendant: () =>
                                        createPickOrganizationMemberPalette({
                                            organizationId,
                                            selectedUserId: filters.requestedReview,
                                            onPick: (userId) =>
                                                onFiltersChange({
                                                    ...filters,
                                                    requestedReview: userId,
                                                }),
                                        }),
                                },
                                ...(withSpaceFilter
                                    ? [
                                          {
                                              key: 'space',
                                              title: 'Space',
                                              icon: Icon.Space,
                                              descendant: pickSpacePalette,
                                          },
                                      ]
                                    : []),
                                ...(docsAgentEnabled
                                    ? [
                                          {
                                              key: 'conversationsIssuesCluster',
                                              title: 'Topic',
                                              icon: Icon.CommentThread,
                                              descendant: () =>
                                                  createPickConversationsIssuesClusterPalette({
                                                      organizationId,
                                                      selectedClusterId:
                                                          filters.conversationsIssuesCluster,
                                                      onPick: (clusterId) => {
                                                          onFiltersChange({
                                                              ...filters,
                                                              conversationsIssuesCluster: clusterId,
                                                          });
                                                      },
                                                  }),
                                          },
                                      ]
                                    : []),
                            ],
                        },
                        ...(hasFilters
                            ? [
                                  {
                                      key: 'reset',
                                      commands: [
                                          {
                                              key: 'clear',
                                              title: 'Clear filters',
                                              icon: Icon.Trashcan,
                                              trigger: () => {
                                                  onFiltersChange({
                                                      ...filters,
                                                      contributor: undefined,
                                                      space: undefined,
                                                      requestedReview: undefined,
                                                      creator: undefined,
                                                      conversationsIssuesCluster: undefined,
                                                  });
                                              },
                                          },
                                      ],
                                  },
                              ]
                            : []),
                    ],
                })
            }
            dot={hasFilters ? 'active' : undefined}
            {...rest}
        />
    );
}

/**
 * Suggestions of filters to apply to the change requests list.
 */
export function ChangeRequestsListFiltersSuggestions(props: {
    filters: ChangeRequestsListFilterState;
    onFiltersChange: (filters: ChangeRequestsListFilterState) => void;
}) {
    const { filters, onFiltersChange } = props;
    const currentUser = useLoggedInUser();
    const withAgentEnabled = useAgentEnabled();

    return (
        <>
            <FilterPill
                label="Yours"
                tooltipLabel="Only show change requests you created"
                filters={filters}
                to={{
                    creator: currentUser.uid,
                }}
                onFiltersChange={onFiltersChange}
            />
            {status === api.ChangeRequestStatus.Open ? (
                <FilterPill
                    label="Awaiting your review"
                    tooltipLabel="Only show change requests awaiting your review"
                    filters={filters}
                    to={{
                        requestedReview: currentUser.uid,
                    }}
                    onFiltersChange={onFiltersChange}
                />
            ) : null}
            {withAgentEnabled ? (
                <FilterPill
                    label="GitBook Agent"
                    leadingIcon={Icon.AIAgent}
                    tooltipLabel="Only show change requests created by the GitBook Agent"
                    filters={filters}
                    to={{
                        creator: api.GitBookUsers.GitbookAgent,
                    }}
                    onFiltersChange={onFiltersChange}
                />
            ) : null}
        </>
    );
}

/**
 * Pill button to toggle quick filters.
 */
function FilterPill(props: {
    label: string;
    tooltipLabel: string;
    leadingIcon?: IconDefinition;
    filters: ChangeRequestsListFilterState;
    to: ChangeRequestsListFilterState;
    onFiltersChange: (filters: ChangeRequestsListFilterState) => void;
}) {
    const { to, filters, label, leadingIcon, tooltipLabel, onFiltersChange } = props;
    const active = Object.entries(to).every(([key, value]) => filters?.[key] === value);

    const onPress = () => {
        onFiltersChange(
            active
                ? {
                      ...filters,
                      // Unset all properties defined in this filter
                      ...Object.fromEntries(Object.entries(to).map(([key]) => [key, undefined])),
                  }
                : {
                      ...filters,
                      // Set all properties defined in this filter
                      ...to,
                  }
        );
    };

    return (
        <Pill
            label={label}
            leadingIcon={leadingIcon}
            active={active}
            onPress={onPress}
            tooltip={{ label: tooltipLabel }}
        />
    );
}
